// document-types.ts

import { CommonModule } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  ActivatedRoute,
  Router
} from '@angular/router';

import { Table } from '../../../shared/table/table';

import { Popup } from '../../../../core/shared/components/popup/popup';

import {
  TableAction,
  TableColumn,
} from '../../../shared/models/table-models';

import { DynamicFieldConfig } from '../../../../core/shared/dynamic-form/form-config.model';

import { AppIcons } from '../../../../core/icons/fa-icons';

import { Setup } from '../../shared/service/setup';
import { BackButton } from "../../../shared/buttons/back-button";

@Component({
  selector: 'app-document-types',
  standalone: true,
  imports: [
    TranslatePipe,
    CommonModule,
    FormsModule,
    Table,
    Popup,
    BackButton
],
  templateUrl: './document-types.html',
  styleUrl: './document-types.css',
})
export class DocumentTypes implements OnInit {

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private setupService: Setup
  ) {}

  // =========================================
  // VARIABLES
  // =========================================

  categorySeq!: number;

  list: any[] = [];

  showPopup = false;

  showDeletePopup = false;

  isEditMode = false;

  formData: any = {};

  selectedDocument: any = null;

  // =========================================
  // INIT
  // =========================================

  ngOnInit(): void {

    this.route.params.subscribe((params) => {

      this.categorySeq =
        +params['id'];

      this.getAllDocuments();

    });

  }

  // =========================================
  // GET DOCUMENTS
  // =========================================

  getAllDocuments(): void {

    this.setupService
      .getAllDocumentByCategry(
        this.categorySeq
      )
      .subscribe({

        next: (res: any[]) => {

          this.list = (res || []).map(
            (item: any) => {

              const efctvDayFlgOn =
                Number(item.efctvDayFlg) === 1;

              return {

                id:
                  item.docSeq,

                name:
                  item.docNm,

                required:
                  item.isReq,

                requiredText:
                  item.isReq
                    ? 'Yes'
                    : 'No',

                efctvDayFlgText:
                  efctvDayFlgOn
                    ? 'Yes'
                    : 'No',

                efctvDaysDisplay:
                  efctvDayFlgOn
                    ? Number(item.efctvDays) || 0
                    : 0,

                issuDtReqFlgText:
                  item.issuDtReqFlg
                    ? 'Yes'
                    : 'No',

                raw: item,

              };

            }
          );

        },

        error: (err) => {

          console.error(err);

        },

      });

  }

  // =========================================
  // TABLE COLUMNS
  // =========================================

  columns: TableColumn[] = [

    {
      key: 'name',
      label: 'SETUP.DOCUMENTS.DOCUMENT_NAME',
    },

    {
      key: 'requiredText',
      label: 'SETUP.DOCUMENTS.REQUIRED',
      map: { Yes: 'COMMON.YES', No: 'COMMON.NO' },
    },

    {
      key: 'efctvDayFlgText',
      label: 'SETUP.DOCUMENTS.EFFECTIVE_DAYS_FLAG',
      map: { Yes: 'COMMON.YES', No: 'COMMON.NO' },
    },

    {
      key: 'efctvDaysDisplay',
      label: 'SETUP.DOCUMENTS.EFFECTIVE_DAYS',
    },

    {
      key: 'issuDtReqFlgText',
      label: 'SETUP.DOCUMENTS.ISSUE_DATE_FLAG',
      map: { Yes: 'COMMON.YES', No: 'COMMON.NO' },
    },

  ];

  // =========================================
  // ACTIONS
  // =========================================

  actions: TableAction[] = [

    {
      icon: AppIcons.edit,

      tooltip: 'COMMON.EDIT',

      class: 'text-indigo-600',

      onClick: (row: any) =>
        this.editDocument(row),
    },

    {
      icon: AppIcons.delete,

      tooltip: 'COMMON.DELETE',

      class: 'text-red-600',

      onClick: (row: any) =>
        this.openDeletePopup(row),
    },

  ];

  // =========================================
  // FORM FIELDS
  // =========================================

  fields: DynamicFieldConfig[] = [

    {
      type: 'text',

      key: 'name',

      label: 'SETUP.DOCUMENTS.DOCUMENT_NAME',

      placeholder:
        'SETUP.DOCUMENTS.ENTER_DOCUMENT_NAME',

      validations: {
        required: true,
      },

      gridClass: 'col-span-1',
    },

    {
      type: 'checkbox',

      key: 'required',

      label: 'SETUP.DOCUMENTS.REQUIRED_DOCUMENT',

      gridClass: 'col-span-1',
    },

    {
      type: 'number',

      key: 'efctvDays',

      label: 'SETUP.DOCUMENTS.EFFECTIVE_DAYS',

      placeholder:
        'SETUP.DOCUMENTS.ENTER_EFFECTIVE_DAYS',

      gridClass: 'col-span-1',

      hidden: true,
    },

    {
      type: 'checkbox',

      key: 'efctvDayFlg',

      label: 'SETUP.DOCUMENTS.EFFECTIVE_DAYS_FLAG',

      gridClass: 'col-span-1',
    },

    {
      type: 'checkbox',

      key: 'issuDtReqFlg',

      label: 'SETUP.DOCUMENTS.ISSUE_DATE_FLAG',

      gridClass: 'col-span-1',
    },

  ];

  // =========================================
  // EFFECTIVE DAYS FIELD VISIBILITY
  // =========================================

  private setEfctvDaysVisibility(flag: any): void {

    const efctvDaysField = this.fields.find(
      (f) => f.key === 'efctvDays'
    );

    if (efctvDaysField) {
      efctvDaysField.hidden = !flag;
    }

  }

  onFormChange(value: any): void {

    this.setEfctvDaysVisibility(value?.efctvDayFlg);

  }

  // =========================================
  // OPEN ADD POPUP
  // =========================================

  openPopup(): void {

    this.isEditMode = false;

    this.formData = {

      required: false,

      efctvDays: 0,

      efctvDayFlg: false,

      issuDtReqFlg: false,

    };

    this.setEfctvDaysVisibility(false);

    this.showPopup = true;

  }

  // =========================================
  // EDIT DOCUMENT
  // =========================================

  editDocument(row: any): void {

    this.isEditMode = true;

    const efctvDayFlg =
      Number(row.raw?.efctvDayFlg) === 1;

    const issuDtReqFlg =
      Boolean(row.raw?.issuDtReqFlg);

    this.formData = {

      id:
        row.id,

      name:
        row.name,

      required:
        row.required,

      efctvDays:
        row.raw?.efctvDays ?? 0,

      efctvDayFlg,

      issuDtReqFlg,

    };

    this.setEfctvDaysVisibility(efctvDayFlg);

    this.showPopup = true;

  }

  // =========================================
  // SAVE DOCUMENT
  // =========================================

handleSave = (data: any): void => {

const payload = {

  docSeq:
    this.isEditMode
      ? this.formData.id
      : null,

  isReq:
    data.required,

  docCtgryKey:
    this.categorySeq,

  docNm:
    data.name,

  efctvDays:
    Number(data.efctvDays) || 0,

  efctvDayFlg:
    data.efctvDayFlg ? 1 : 0,

  issuDtReqFlg:
    !!data.issuDtReqFlg,

  delFlg: false,

};

  this.setupService
    .saveDocument(payload)
    .subscribe({

      next: () => {

        this.showPopup = false;

        this.getAllDocuments();

      },

      error: (err) => {

        console.error(err);

      },

    });

};

  // =========================================
  // OPEN DELETE POPUP
  // =========================================

  openDeletePopup(row: any): void {

    this.selectedDocument = row;

    this.showDeletePopup = true;

  }

  // =========================================
  // CONFIRM DELETE
  // =========================================

confirmDelete(): void {

  if (!this.selectedDocument) {
    return;
  }

  const payload = {

    // selected document seq
    docSeq:
      this.selectedDocument.id,

    isReq:
      this.selectedDocument.required,

    docCtgryKey:
      this.categorySeq,

    docNm:
      this.selectedDocument.name,

    efctvDays:
      this.selectedDocument.raw?.efctvDays ?? 0,

    efctvDayFlg:
      Number(this.selectedDocument.raw?.efctvDayFlg) === 1 ? 1 : 0,

    issuDtReqFlg:
      Boolean(this.selectedDocument.raw?.issuDtReqFlg),

    // delete flag true
    delFlg: true,

  };

  this.setupService
    .saveDocument(payload)
    .subscribe({

      next: () => {

        this.showDeletePopup = false;

        this.selectedDocument = null;

        this.getAllDocuments();

      },

      error: (err) => {

        console.error(err);

      },

    });

}

}